import {
  get,
  post,
  type APIRequestBody,
  type APIRequestPathParams,
  type APIRequestQueryParams,
  type APIResponse,
} from './base-fetch';
import type {
  CompanyFreeTextField,
  CompanyOfficeLocation,
} from './organization.api';
import type { CompanyProjectsListings } from './organization-projects-listings.api';

export type SpotlightCompany =
  APIResponse<'/api/v4/spotlight/organizations/list'>[number];

type SpotlightCompaniesKpis = APIResponse<'/api/v4/spotlight/kpis'>;

type SpotlightOrganizationsQueryParams =
  APIRequestQueryParams<'/api/v4/spotlight/organizations/list'>;

export type SpotlightCompanyData =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}'>;

export type SpotlightCompanyProducts =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/products'>;

export type SpotlightCompanyFundings =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/fundings'>;

export type SpotlightOrganizationInvestors =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/investors'>;

export type SpotlightCompanyClients =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/clients'>;

export type SpotlightCompanyContacts =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/contacts'>;

export type SpotlightCompanyContactsQueryParams =
  APIRequestQueryParams<'/api/v4/spotlight/organizations/{organizationId}/contacts'>;

export type SpotlightCompanyExternalLinks =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/external-links'>;

export type SpotlightCompanyRatings =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/ratings'>;

export type SpotlightCompanyTags =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/tags'>;

export type SpotlightCompanyOfficeLocations = CompanyOfficeLocation[];

export type SpotlightCompanyNews =
  APIResponse<'/api/v4/organization/{organizationId}/news'>;

export type SpotlightCompanyNewsQueryParams =
  APIRequestQueryParams<'/api/v4/organization/{organizationId}/news'>;

export type SpotlightCompanyProjectListings = CompanyProjectsListings;

export type SpotlightCompanyFreeTextFields = CompanyFreeTextField[];

export type SendSpotlightOrganizationInquiryPayload =
  APIRequestBody<'/api/v4/spotlight/organizations/{organizationId}/send-inquiry'>;

export const fetchSpotlightCompanies = async (
  params: SpotlightOrganizationsQueryParams,
) =>
  get<SpotlightCompany[]>('/v4/spotlight/organizations/list', {
    params,
  });

export const fetchSpotlightCompaniesKpis = async (
  params: SpotlightOrganizationsQueryParams,
) =>
  get<SpotlightCompaniesKpis>('/v4/spotlight/kpis', {
    params,
  });

export const fetchSpotlightCompany = async (organizationId: string) =>
  get<SpotlightCompanyData>(`/v4/spotlight/organizations/${organizationId}`);

export const fetchSpotlightCompanyProducts = async (organizationId: string) =>
  get<SpotlightCompanyProducts>(
    `/v4/spotlight/organizations/${organizationId}/products`,
  );

export const fetchSpotlightCompanyFundings = async (organizationId: string) =>
  get<SpotlightCompanyFundings>(
    `/v4/spotlight/organizations/${organizationId}/fundings`,
  );

export const fetchSpotlightOrganizationInvestors = async (
  organizationId: string,
) =>
  get<SpotlightOrganizationInvestors>(
    `/v4/spotlight/organizations/${organizationId}/investors`,
  );

export const fetchSpotlightCompanyClients = async (organizationId: string) =>
  get<SpotlightCompanyClients>(
    `/v4/spotlight/organizations/${organizationId}/clients`,
  );

export const fetchSpotlightCompanyContacts = async (
  organizationId: string,
  params: SpotlightCompanyContactsQueryParams,
) =>
  get<SpotlightCompanyContacts>(
    `/v4/spotlight/organizations/${organizationId}/contacts`,
    { params },
  );

export const fetchSpotlightCompanyExternalLinks = async (
  organizationId: string,
) =>
  get<SpotlightCompanyExternalLinks>(
    `/v4/spotlight/organizations/${organizationId}/external-links`,
  );

export const fetchSpotlightCompanyRatings = async (organizationId: string) =>
  get<SpotlightCompanyRatings>(
    `/v4/spotlight/organizations/${organizationId}/ratings`,
  );

export const fetchSpotlightCompanyTags = async (organizationId: string) =>
  get<SpotlightCompanyTags>(
    `/v4/spotlight/organizations/${organizationId}/tags`,
  );

export const fetchSpotlightCompanyOfficeLocations = async (
  organizationId: string,
) =>
  get<SpotlightCompanyOfficeLocations>(
    `/v4/spotlight/organizations/${organizationId}/office-locations`,
  );

export const fetchSpotlightCompanyNews = async (
  organizationId: string,
  params: SpotlightCompanyNewsQueryParams,
) =>
  get<SpotlightCompanyNews>(
    `/v4/spotlight/organizations/${organizationId}/news`,
    { params },
  );

export const fetchSpotlightCompanyProjectListings = async (
  organizationId: string,
) =>
  get<SpotlightCompanyProjectListings>(
    `/v4/spotlight/organizations/${organizationId}/project-listings`,
  );

export const fetchSpotlightCompanyFreeTextFields = async (
  organizationId: string,
) =>
  get<SpotlightCompanyFreeTextFields>(
    `/v4/spotlight/organizations/${organizationId}/free-text-fields`,
  );

export const sendSpotlightOrganizationInquiry = (
  organizationId: string,
  payload: SendSpotlightOrganizationInquiryPayload,
) =>
  post<void>(
    `/v4/spotlight/organizations/${organizationId}/send-inquiry`,
    payload,
  );

type SpotlightOrganizationFile =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/files'>[number];

type SpotlightOrganizationFileLink =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/files/{fileId}/link'>;

type SpotlightOrganizationFileLinkParams =
  APIRequestPathParams<'/api/v4/spotlight/organizations/{organizationId}/files/{fileId}/link'>;

type SpotlightOrganizationFileLinkQueries =
  APIRequestQueryParams<'/api/v4/spotlight/organizations/{organizationId}/files/{fileId}/link'>;

type SpotlightOrganizationFileScanningStatus =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/files/{fileId}/scanning-status'>;

type SpotlightOrganizationActionsInfo =
  APIResponse<'/api/v4/spotlight/organizations/{organizationId}/actions'>;

export const fetchSpotlightOrganizationFiles = async (organizationId: string) =>
  get<SpotlightOrganizationFile[]>(
    `/v4/spotlight/organizations/${organizationId}/files`,
  );

export const fetchSpotlightOrganizationFileLink = async (
  pathParams: SpotlightOrganizationFileLinkParams,
  queryParams: SpotlightOrganizationFileLinkQueries,
) =>
  get<SpotlightOrganizationFileLink>(
    `/v4/spotlight/organizations/${pathParams.organizationId}/files/${pathParams.fileId}/link`,
    { params: queryParams },
  );

export const fetchSpotlightOrganizationFileScanningStatus = async ({
  fileId,
  organizationId,
}: {
  fileId: string;
  organizationId: string;
}) =>
  get<SpotlightOrganizationFileScanningStatus>(
    `/v4/spotlight/organizations/${organizationId}/files/${fileId}/scanning-status`,
  );

export const fetchSpotlightOrganizationActionsInfo = async (
  organizationId: string,
) =>
  get<SpotlightOrganizationActionsInfo>(
    `/v4/spotlight/organizations/${organizationId}/actions`,
  );
